'use client';

/**
 * Site-wide footer. Renders at the bottom of every public route (AppShell
 * skips it on /auth/* and /admin/*). Holds:
 *  - Tagline, social icons, and copyright (auto-year)
 *  - Three link sections: Product, Legal, Resources
 *  - "Cookie settings" button — re-opens the consent modal
 *  - Mailto for support email (only if admin has configured one)
 *
 * Social URLs and the support email come from the runtime site_settings
 * registry, so admins can edit them in /admin → Config → Social/Contact
 * without a redeploy. Empty values render nothing.
 *
 * All copy goes through `useT()` so EN/TH toggle works without a reload.
 */

import Link from 'next/link';
import { useT } from '@/lib/i18n';
import { useCookieConsent } from '@/lib/cookie-consent';
import { useSiteConfig } from '@/lib/site-config';
import { isSafeHttpUrl } from '@/lib/site-config-server';
import { SITE } from '@/lib/site';

// Material symbols icon names per platform — kept here (not in i18n) because
// they're identifiers, not user-facing copy.
const SOCIAL_ICONS: Record<string, string> = {
  twitter: 'flutter_dash',  // closest match in Material Symbols; consider a real X icon later
  github: 'code',
  linkedin: 'work',
  instagram: 'photo_camera',
  youtube: 'play_circle',
};

const SOCIAL_LABELS: Record<string, string> = {
  twitter: 'Twitter / X',
  github: 'GitHub',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  youtube: 'YouTube',
};

type SocialKey = 'twitter' | 'github' | 'linkedin' | 'instagram' | 'youtube';
const SOCIAL_ORDER: readonly SocialKey[] = ['twitter', 'github', 'linkedin', 'instagram', 'youtube'];

export default function Footer() {
  const t = useT();
  const { openSettings } = useCookieConsent();
  const { config } = useSiteConfig();
  const year = new Date().getFullYear();

  // Only http(s) URLs reach the DOM. A compromised admin setting
  // `social.twitter_url = "javascript:..."` would otherwise XSS every
  // visitor on click; `isSafeHttpUrl` rejects anything that isn't a
  // plain http/https URL.
  const socialLinks = SOCIAL_ORDER
    .map((key) => ({
      key,
      url: (config[`social.${key}_url` as keyof typeof config] as string)?.trim?.() ?? '',
    }))
    .filter((s) => isSafeHttpUrl(s.url));

  // Support / security email — we render these inside `mailto:` hrefs and as
  // visible text. JSX escapes attribute values, so the worst an attacker can
  // do with a malformed email is make the mailto link non-functional. We
  // still strip any control chars (CRLF defense) for safety.
  const cleanEmail = (raw: string) => raw.replace(/[\r\n\t]/g, '').trim();
  const supportEmail = cleanEmail(config['contact.support_email'] || '');
  const securityEmail = cleanEmail(config['contact.security_email'] || '');

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: 'var(--ink-3)',
    margin: '0 0 12px',
  };

  const linkStyle: React.CSSProperties = {
    color: 'var(--ink-2)',
    textDecoration: 'none',
    fontSize: 14,
    lineHeight: 2,
  };

  return (
    <footer
      style={{
        marginTop: 64,
        padding: '40px 24px 32px',
        background: 'var(--bg-soft)',
        borderTop: '1px solid var(--line)',
        // Reserve footer height so it doesn't pop in late and shove the
        // viewport down. Async-loaded social icons + admin-config-driven
        // links were the #1 CLS culprit (Lighthouse: 0.480 — half of all
        // shift on the page came from here). 320px covers the worst case
        // (mobile, all 4 link columns stacked); on desktop the actual
        // height is ~280px so this is just slack, never extra blank space.
        minHeight: 320,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 32,
          marginBottom: 32,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'var(--font-headline)',
              fontSize: 20,
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            {SITE.name}
          </div>
          <div style={{ color: 'var(--ink-2)', fontSize: 14, lineHeight: 1.6, marginBottom: 12 }}>
            {t('footer.tagline')}
          </div>
          {socialLinks.length > 0 && (
            <div style={{ display: 'flex', gap: 8 }}>
              {socialLinks.map(({ key, url }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer me"
                  aria-label={SOCIAL_LABELS[key]}
                  title={SOCIAL_LABELS[key]}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-full)',
                    border: '1px solid var(--line)',
                    color: 'var(--ink-2)',
                    background: 'var(--bg-raised)',
                    textDecoration: 'none',
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                    {SOCIAL_ICONS[key]}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>

        <nav aria-label={t('footer.section_product')}>
          <h2 style={sectionTitleStyle}>{t('footer.section_product')}</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li><Link href="/explore" style={linkStyle}>{t('footer.link_explore')}</Link></li>
            <li><Link href="/trending" style={linkStyle}>{t('footer.link_trending')}</Link></li>
            <li><Link href="/collections" style={linkStyle}>{t('footer.link_collections')}</Link></li>
            <li><Link href="/tags" style={linkStyle}>{t('footer.link_tags')}</Link></li>
          </ul>
        </nav>

        <nav aria-label={t('footer.section_legal')}>
          <h2 style={sectionTitleStyle}>{t('footer.section_legal')}</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li><Link href="/privacy" style={linkStyle}>{t('footer.link_privacy')}</Link></li>
            <li><Link href="/terms" style={linkStyle}>{t('footer.link_terms')}</Link></li>
            <li><Link href="/cookies" style={linkStyle}>{t('footer.link_cookies')}</Link></li>
            <li><Link href="/guidelines" style={linkStyle}>{t('footer.link_guidelines')}</Link></li>
            <li><Link href="/dmca" style={linkStyle}>{t('footer.link_dmca')}</Link></li>
            <li><Link href="/disclaimer" style={linkStyle}>{t('footer.link_disclaimer')}</Link></li>
          </ul>
        </nav>

        <nav aria-label={t('footer.section_resources')}>
          <h2 style={sectionTitleStyle}>{t('footer.section_resources')}</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li><Link href="/about" style={linkStyle}>{t('footer.link_about')}</Link></li>
            {supportEmail && (
              <li>
                <a href={`mailto:${supportEmail}`} style={linkStyle}>
                  {supportEmail}
                </a>
              </li>
            )}
            {securityEmail && (
              <li>
                <a href="/.well-known/security.txt" style={linkStyle}>
                  {t('footer.link_security')}
                </a>
              </li>
            )}
            <li>
              <button
                type="button"
                onClick={openSettings}
                style={{
                  ...linkStyle,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                }}
              >
                {t('cookie.settings_button')}
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          paddingTop: 20,
          borderTop: '1px solid var(--line)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 13,
          color: 'var(--ink-3)',
        }}
      >
        <span>{t('footer.copyright', { year, site: SITE.name })}</span>
        <span>{t('footer.made_with')}</span>
      </div>
    </footer>
  );
}
